import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useFlag } from '@modernloop/shared/feature-flag';
import { useGenericOnError } from '@modernloop/shared/hooks';
import { cloneDeep } from 'lodash';

import {
  DynamicAudience,
  InterviewMeetingLocationSettingsInput,
  InterviewMeetingLocationType,
  RemoteAttachmentInput,
  SelfScheduleOptionsUpsertInput,
  useSelfScheduleOptionsUpsertMutation,
  useSelfScheduleTaskCreateMutation,
} from 'src/generated/mloop-graphql';

import { useCreateLocationHeader } from 'src/hooks/amplitude/useCreateLocationHeader';
import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useAuth from 'src/hooks/useAuth';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { FileUploadFlow } from 'src/slices/files';

import { getFilesByUploadFlow } from 'src/store/selectors/files';

import { useSelector } from 'src/store';

import useRefetchTaskListQuery from '../ApplicationDetailsTaskView/ApplicationContent/useRefetchTaskList';
import { TaskCreateInputWrapper, getTaskCreateInput } from '../ScheduleTask/CreateModals/shared/types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CREATE_TASK_MUTATION = gql`
  mutation SelfScheduleTaskCreate($input: TaskCreateInput!) {
    taskCreate(input: $input) {
      task {
        id
      }
    }
  }
`;

/**
 * Filters down the interview meeting location setting object to only include the fields that are needed for the mutation.
 * @param meetingLocationSetting - The input interview meeting location setting.
 * @returns The new interview meeting location setting object.
 */
const createInterviewMeetingLocationSetting = (
  meetingLocationSetting: InterviewMeetingLocationSettingsInput | undefined
): InterviewMeetingLocationSettingsInput | undefined => {
  if (!meetingLocationSetting) {
    return undefined;
  }

  switch (meetingLocationSetting.interviewMeetingLocationType) {
    case InterviewMeetingLocationType.GoogleMeet:
    case InterviewMeetingLocationType.Zoom:
    case InterviewMeetingLocationType.MicrosoftTeams:
      return {
        interviewMeetingLocationType: meetingLocationSetting.interviewMeetingLocationType,
        dynamicHost: meetingLocationSetting.dynamicHost,
        hostEmployeeId: meetingLocationSetting.hostEmployeeId,
        remoteVideoMeetingHostUserId: meetingLocationSetting.remoteVideoMeetingHostUserId,
      };
    case InterviewMeetingLocationType.CustomLink:
    case InterviewMeetingLocationType.CustomPhone:
    case InterviewMeetingLocationType.CustomText:
      return {
        interviewMeetingLocationType: meetingLocationSetting.interviewMeetingLocationType,
        customLocation: meetingLocationSetting.customLocation,
      };
    case InterviewMeetingLocationType.CandidatePhone:
      return {
        interviewMeetingLocationType: meetingLocationSetting.interviewMeetingLocationType,
      };
    case InterviewMeetingLocationType.None:
    case InterviewMeetingLocationType.NotSpecified:
    default:
      return {
        interviewMeetingLocationType: meetingLocationSetting.interviewMeetingLocationType,
      };
  }
};

const useSelfScheduleOptionsUpsert = (
  selfScheduleOptions: SelfScheduleOptionsUpsertInput,
  taskId?: string,
  taskInput?: TaskCreateInputWrapper
) => {
  const auth = useAuth();
  const handleError = useGenericOnError();
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');

  const activityFeedRefetch = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();
  const refetchTaskList = useRefetchTaskListQuery();
  const files = useSelector((state) => getFilesByUploadFlow(state, FileUploadFlow.SELF_SCHEDULE_LINK));

  const [selfScheduleOptionsUpsertMutation] = useSelfScheduleOptionsUpsertMutation({
    onError: (error) => {
      handleError({ error });
    },
  });

  const createLocationHeader = useCreateLocationHeader();
  const [createTask] = useSelfScheduleTaskCreateMutation({
    context: {
      headers: {
        ...createLocationHeader,
      },
    },
  });

  const selfScheduleOptionsUpsert = useCallback(
    async (
      selectedToFreeformEmail: string,
      ccFreeform: string[],
      bccFreeform: string[],
      ccEmployeeIds: string[],
      bccEmployeeIds: string[],
      selectedTemplateId: string | undefined,
      subject: string,
      bodyHtml: string,
      saveWithoutSending: boolean,
      ccDynamicAudience: DynamicAudience[],
      bccDynamicAudience: DynamicAudience[]
    ): Promise<string | null> => {
      if (!auth.userInfo?.employeeEmail) {
        return null;
      }

      const selfScheduleOptionsClone = cloneDeep(selfScheduleOptions);

      if (!selfScheduleOptionsClone.options) {
        return null;
      }

      const { candidateCalendarId, interviewerCalendarId, location } = selfScheduleOptionsClone.options;
      if (!candidateCalendarId || !interviewerCalendarId || !location) {
        return null;
      }

      const attachments: RemoteAttachmentInput[] = [];

      if (files) {
        files.forEach((file) => {
          if (!file.path) {
            return;
          }
          attachments.push({ name: file.name, path: file.path });
        });
      }

      let finalTaskId = taskId;
      if (!taskId && taskInput) {
        delete taskInput.taskCreatorId;
        const saveInput = getTaskCreateInput(taskInput, orgBreakImprovementsEnabled);
        const createResult = await createTask({
          variables: {
            input: saveInput,
          },
          onError: (error) => {
            handleError({ error });
          },
        });
        finalTaskId = createResult.data?.taskCreate?.task?.id;
      }

      await selfScheduleOptionsUpsertMutation({
        variables: {
          input: {
            taskId: finalTaskId,
            candidateEmailTemplateId: selfScheduleOptions.candidateEmailTemplateId,
            candidateEventTemplateId: selfScheduleOptions.candidateEventTemplateId,
            interviewerEventTemplateId: selfScheduleOptions.interviewerEventTemplateId,
            options: {
              ...selfScheduleOptionsClone.options,
              candidateCalendarId,
              interviewerCalendarId,
              location,
              candidateEventAdditionalAttendees: selfScheduleOptionsClone.options.candidateEventAdditionalAttendees
                ? {
                    employeeIds: selfScheduleOptionsClone.options.candidateEventAdditionalAttendees.employeeIds || [],
                    dynamicAudiences:
                      selfScheduleOptionsClone.options.candidateEventAdditionalAttendees.dynamicAudiences || [],
                    externalEmailAddresses:
                      selfScheduleOptionsClone.options.candidateEventAdditionalAttendees.externalEmailAddresses || [],
                  }
                : undefined,
              interviewerEventAdditionalAttendees: selfScheduleOptionsClone.options.interviewerEventAdditionalAttendees
                ? {
                    employeeIds: selfScheduleOptionsClone.options.interviewerEventAdditionalAttendees.employeeIds || [],
                    dynamicAudiences:
                      selfScheduleOptionsClone.options.interviewerEventAdditionalAttendees.dynamicAudiences || [],
                    externalEmailAddresses:
                      selfScheduleOptionsClone.options.interviewerEventAdditionalAttendees.externalEmailAddresses || [],
                  }
                : undefined,
            },
            interviewMeetingLocationSetting: selfScheduleOptions.interviewMeetingLocationSetting
              ? createInterviewMeetingLocationSetting(selfScheduleOptions.interviewMeetingLocationSetting)
              : undefined,
            candidateEmailContent: saveWithoutSending
              ? undefined
              : {
                  emailTemplateId: selectedTemplateId,
                  description: bodyHtml,
                  summary: subject,
                  toEmailAddress: selectedToFreeformEmail,
                  ccEmployeeIds,
                  bccEmployeeIds,
                  ccExternalAddresses: ccFreeform,
                  bccExternalAddresses: bccFreeform,
                  ccDynamicAudiences: ccDynamicAudience,
                  bccDynamicAudiences: bccDynamicAudience,
                  attachments,
                },
          },
        },
      });

      if (finalTaskId) {
        refetchScheduleTaskQuery();
        activityFeedRefetch();
        refetchTaskList();
        return finalTaskId;
      }

      return null;
    },
    [
      auth.userInfo?.employeeEmail,
      selfScheduleOptions,
      files,
      taskId,
      taskInput,
      selfScheduleOptionsUpsertMutation,
      orgBreakImprovementsEnabled,
      createTask,
      handleError,
      refetchScheduleTaskQuery,
      activityFeedRefetch,
      refetchTaskList,
    ]
  );

  return selfScheduleOptionsUpsert;
};

export default useSelfScheduleOptionsUpsert;
